import { Button, Col, Form, Modal, Row, Spinner } from 'react-bootstrap'
import Select from 'react-select'
import { useFormik } from 'formik'
import { useEffect } from 'react'
import { useMutation, useQuery } from '@apollo/client'
import { toast } from 'react-toastify'
import * as Yup from 'yup'
import { CREATE_LESSON, UPDATE_LESSON } from '../../../gql/mutations/academyMutations'
import { SEARCH_RESOURCES_CENTER } from '../../../gql/queries/resourceCenterQuery'
import {
  ICreateLessonResponse,
  ILesson,
  IUpdateLessonResponse,
} from '../../../types/academy'
import { IResourceCenter } from '../../../types/resourceCenter'
import { parseId } from '../../../helpers'
import { getResourceTypeIcon } from '../../resourceCenter/resourceTypeHelpers'
import { IntlShape, useIntl } from 'react-intl'
import { ACADEMY_MESSAGES as A, BACKEND_ERROR, REQUIRED_MARK } from '../academyMessages'

/** Fabrica: el mensaje necesita `intl`, asi que el esquema no puede ser un const. */
const buildValidationSchema = (intl: IntlShape) =>
  Yup.object().shape({
    resourceId: Yup.string().required(intl.formatMessage(A.vContentRequired)),
  })

interface ISearchResourcesCenterResponse {
  resourcesCenterAdvancedSearch: IResourceCenter[]
}

interface IModalProps {
  isOpen: boolean
  onClose: () => void
  refetch: () => void
  courseId: string
  // Si viene lesson, es edición; si no, creación
  lesson?: ILesson | null
}

export const ModalLessonForm: React.FC<IModalProps> = ({
  isOpen,
  onClose,
  refetch,
  courseId,
  lesson,
}) => {
  const intl = useIntl()
  const isEdit = Boolean(lesson)

  const [createLesson, { loading: creating }] = useMutation<ICreateLessonResponse>(CREATE_LESSON)
  const [updateLesson, { loading: updating }] = useMutation<IUpdateLessonResponse>(UPDATE_LESSON)
  const loading = creating || updating

  // Recursos del Resource Center para usar como contenido de la lección
  const { data: dataResources, loading: loadingResources } =
    useQuery<ISearchResourcesCenterResponse>(SEARCH_RESOURCES_CENTER, {
      variables: { isActive: true, isCampaignMedia: false },
      fetchPolicy: 'cache-first',
    })

  const formik = useFormik({
    initialValues: {
      resourceId: lesson?.resource?.id || '',
      title: lesson?.title || '',
      isRequired: lesson?.isRequired ?? true,
    },
    enableReinitialize: true,
    validationSchema: buildValidationSchema(intl),
    onSubmit: async (v) => {
      try {
        if (isEdit && lesson) {
          const result = await updateLesson({
            variables: {
              id: parseId(lesson.id),
              lessonData: {
                courseId: parseId(courseId),
                resourceId: parseId(v.resourceId),
                title: v.title || '',
                isRequired: v.isRequired,
              },
            },
          })
          if (result.data?.updateLesson.success) {
            toast.success(intl.formatMessage(A.toastLessonUpdated))
            onClose()
            refetch()
          } else if (result.data?.updateLesson.errors?.length) {
            toast.error(intl.formatMessage(BACKEND_ERROR, {message: result.data.updateLesson.errors[0].message}))
          }
        } else {
          const result = await createLesson({
            variables: {
              lessonData: {
                courseId: parseId(courseId),
                resourceId: parseId(v.resourceId),
                title: v.title || '',
                isRequired: v.isRequired,
              },
            },
          })
          if (result.data?.createLesson.success) {
            toast.success(intl.formatMessage(A.toastLessonAdded))
            onClose()
            refetch()
            formik.resetForm()
          } else if (result.data?.createLesson.errors?.length) {
            toast.error(intl.formatMessage(BACKEND_ERROR, {message: result.data.createLesson.errors[0].message}))
          }
        }
      } catch (err) {
        console.error('Error saving lesson:', err)
        toast.error(intl.formatMessage(A.errSaveLesson))
      }
    },
  })

  useEffect(() => {
    if (!isOpen) formik.resetForm()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  const resourceOptions =
    dataResources?.resourcesCenterAdvancedSearch.map((resource) => ({
      label: `${getResourceTypeIcon(resource.resourceType)} ${resource.name}`,
      value: resource.id,
    })) || []

  return (
    <Modal show={isOpen} onHide={onClose} size="lg" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title className="d-flex align-items-center gap-2">
          <i className={`bi ${isEdit ? 'bi-pencil-square' : 'bi-plus-circle'}`} />
          {intl.formatMessage(isEdit ? A.formEditLesson : A.formAddLesson)}
          {loading && <Spinner animation="border" size="sm" />}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Form onSubmit={formik.handleSubmit}>
          <Row>
            <Col md={12}>
              <Form.Group className="mb-3">
                <Form.Label className="fw-bold">
                  {intl.formatMessage(A.contentLabel)}
                  <span className="text-danger ms-1">{REQUIRED_MARK}</span>
                </Form.Label>
                <Select
                  isLoading={loadingResources}
                  value={resourceOptions.find((opt) => opt.value === formik.values.resourceId) || null}
                  onChange={(option) => formik.setFieldValue('resourceId', option?.value || '')}
                  options={resourceOptions}
                  placeholder={intl.formatMessage(A.contentPlaceholder)}
                />
                {formik.errors.resourceId && formik.touched.resourceId && (
                  <Form.Text className="text-danger">{formik.errors.resourceId}</Form.Text>
                )}
                <Form.Text className="text-muted">
                  {intl.formatMessage(A.contentHint)}
                </Form.Text>
              </Form.Group>
            </Col>

            <Col md={12}>
              <Form.Group className="mb-3">
                <Form.Label className="fw-bold">{intl.formatMessage(A.customTitle)}</Form.Label>
                <Form.Control
                  name="title"
                  placeholder={intl.formatMessage(A.customTitlePlaceholder)}
                  value={formik.values.title}
                  onChange={formik.handleChange}
                />
              </Form.Group>
            </Col>

            <Col md={12}>
              <Form.Group className="mb-3">
                <Form.Check
                  type="checkbox"
                  label={intl.formatMessage(A.requiredToComplete)}
                  checked={formik.values.isRequired}
                  onChange={(e) => formik.setFieldValue('isRequired', e.target.checked)}
                />
              </Form.Group>
            </Col>
          </Row>
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onClose} disabled={loading}>
          {intl.formatMessage(A.cancel)}
        </Button>
        <Button variant="primary" onClick={() => formik.handleSubmit()} disabled={loading}>
          {loading ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              {intl.formatMessage(A.saving)}
            </>
          ) : (
            <>
              <i className="bi bi-check-lg me-1" />
              {intl.formatMessage(isEdit ? A.saveChanges : A.formAddLesson)}
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
